import { useEffect, useRef, useState, useMemo } from 'react';
import type { DraftState, Manager, TeamLiveData } from '../types';
import { TEAMS_BY_ABBR, teamFullName } from '../constants/teams';
import { maxBidPower, nextBidderId, findManager } from '../lib/draftLogic';
import { type TeamValuation, valueRating } from '../lib/valuation';
import { evaluateBidOpportunity, type AdvisorSettings, DEFAULT_ADVISOR_SETTINGS } from '../lib/aiAdvisor';
import { sounds } from '../lib/soundEffects';
import { TeamLogo } from './TeamLogo';
import { SuggestedValueBadge } from './SuggestedValueBadge';
import { AIAdvisorCard } from './AIAdvisorCard';
import { TeamScoutingCard, type PriorSaleHistory } from './TeamScoutingCard';

const SHOT_CLOCK_SECONDS = 30;

interface Props {
  state: DraftState;
  localManager: Manager | null;
  isCommissioner: boolean;
  pastPrices: Record<string, number>;
  liveData: Record<string, TeamLiveData>;
  rankings: Record<string, number>;
  valuations: Record<string, TeamValuation>;
  settings?: AdvisorSettings;
  pastSalesByTeam?: Record<string, PriorSaleHistory[]>;
  onBid: (amount: number) => Promise<void>;
  onPass: () => Promise<void>;
  onSell: () => Promise<void>;
  onVeto: () => void;
  onOpenWarRoom?: () => void;
  onOpenSettings?: () => void;
}

export function BiddingArea({
  state, localManager, isCommissioner, pastPrices, liveData, rankings, valuations,
  settings = DEFAULT_ADVISOR_SETTINGS, pastSalesByTeam,
  onBid, onPass, onSell, onVeto, onOpenWarRoom, onOpenSettings,
}: Props) {
  const { auction } = state;
  const team = auction.team!;
  const highBidder = findManager(state, auction.highBidderId);
  const nextId = nextBidderId(state);
  const isMyTurn = !!localManager && nextId === localManager.id;
  const isHighBidder = localManager?.id === auction.highBidderId;
  const allOthersOut = nextId === null;
  const power = localManager ? maxBidPower(state, localManager.id) : 0;
  const nextRequired = auction.highBid + 1;
  const past = pastPrices[team];
  const rank = rankings[team];

  const [bid, setBid] = useState(nextRequired);
  useEffect(() => { setBid(nextRequired); }, [auction.highBid]);

  // --- Shot clock: counts down 30s; resets every time the high bid changes
  // (i.e. someone bids) or a fresh auction starts. Visual only — no auto-action.
  const [now, setNow] = useState(() => Date.now());
  const clockStartRef = useRef<number>(Date.now());
  useEffect(() => {
    clockStartRef.current = Date.now();
    setNow(Date.now());
  }, [auction.highBid, auction.team, auction.passers.length]);
  useEffect(() => {
    const i = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(i);
  }, []);
  const elapsedMs = now - clockStartRef.current;
  const remainingMs = Math.max(0, SHOT_CLOCK_SECONDS * 1000 - elapsedMs);
  const remainingSec = Math.ceil(remainingMs / 1000);
  const clockPct = (remainingMs / (SHOT_CLOCK_SECONDS * 1000)) * 100;
  const clockColor =
    remainingSec <= 5 ? 'bg-rose-500' : remainingSec <= 10 ? 'bg-amber-500' : 'bg-teal-500';
  const clockTextColor =
    remainingSec <= 5 ? 'text-rose-300 animate-pulse' : remainingSec <= 10 ? 'text-amber-300' : 'text-teal-300';

  // Sound cues for critical clock countdown
  const lastTickSecRef = useRef<number>(-1);
  useEffect(() => {
    if (!allOthersOut && remainingSec <= 5 && remainingSec > 0 && remainingSec !== lastTickSecRef.current) {
      lastTickSecRef.current = remainingSec;
      sounds.playClockTick();
    }
  }, [remainingSec, allOthersOut]);

  // Turn alert sound
  const wasMyTurnRef = useRef(false);
  useEffect(() => {
    if (isMyTurn && !wasMyTurnRef.current) {
      sounds.playYourTurn();
    }
    wasMyTurnRef.current = isMyTurn;
  }, [isMyTurn]);

  const canAfford = power >= nextRequired;
  const valuation = valuations[team];
  const valueAtCurrent = valuation ? valueRating(valuation.suggested, auction.highBid) : null;
  const valueAtMyBid = valuation ? valueRating(valuation.suggested, bid) : null;

  // AI Evaluation for Rob / current user
  const targetManagerId = localManager?.id ?? 'rob';
  const aiDecision = useMemo(() => {
    return evaluateBidOpportunity(
      state,
      targetManagerId,
      team,
      auction.highBid,
      auction.highBidderId,
      liveData,
      valuations,
      settings,
    );
  }, [state, targetManagerId, team, auction.highBid, auction.highBidderId, liveData, valuations, settings]);

  const handleApplyAIBid = (amount: number) => {
    setBid(amount);
    if (isMyTurn && canAfford && amount >= nextRequired && amount <= power) {
      onBid(amount);
    }
  };

  return (
    <div className="panel p-5 border-2 border-rose-500/80">
      <div className="flex items-center gap-4 mb-4">
        <TeamLogo abbr={team} size={64} className="drop-shadow-lg" />
        <div className="flex-1">
          <div className="text-xs uppercase text-rose-400 font-bold tracking-wider">🔥 Live Auction</div>
          <h2 className="text-3xl font-extrabold text-white">{teamFullName(team)}</h2>
          <div className="flex flex-wrap gap-2 text-xs mt-1 items-center">
            {valuation && <SuggestedValueBadge v={valuation} />}
            {past != null && <span className="text-amber-400">Last yr ${past}</span>}
            {liveData[team]?.projectedWins != null && (
              <span className="text-slate-400">Proj {liveData[team].projectedWins!.toFixed(1)}W</span>
            )}
            {rank && <span className="text-slate-400">Rk #{rank}</span>}
            {liveData[team]?.record && (
              <span className="text-slate-400">
                {liveData[team].record!.wins}-{liveData[team].record!.losses}
              </span>
            )}
            {valueAtCurrent && auction.highBid > 0 && (
              <span className={`pill text-[10px] text-white ${valueAtCurrent.bgClass}`}>
                Current bid: {valueAtCurrent.label}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <Tile label="High Bid" value={`$${auction.highBid}`} accent="text-rose-400" />
        <Tile label="Leader" value={highBidder ? `${highBidder.emoji ?? ''} ${highBidder.name}` : '—'} accent="text-amber-400" />
        <Tile label="Your Max" value={`$${power}`} accent={canAfford ? 'text-teal-400' : 'text-rose-400'} />
      </div>

      {/* Prior Years Sales & Team Scouting Intel */}
      <TeamScoutingCard
        abbr={team}
        pastSales={pastSalesByTeam?.[team]}
      />

      {/* Rob's Dedicated AI Advisor Card */}
      <AIAdvisorCard
        decision={aiDecision}
        isMyTurn={isMyTurn}
        canAfford={canAfford}
        nextRequired={nextRequired}
        onApplyBid={handleApplyAIBid}
        onOpenWarRoom={onOpenWarRoom}
        onOpenSettings={onOpenSettings}
      />

      {/* Shot clock */}
      {!allOthersOut && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-[10px] uppercase mb-1">
            <span className="text-slate-500">⏱ Shot Clock</span>
            <span className={`font-extrabold text-base ${clockTextColor}`}>{remainingSec}s</span>
          </div>
          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full transition-[width] duration-200 ease-linear ${clockColor}`}
              style={{ width: `${clockPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Passers */}
      {auction.passers.length > 0 && (
        <div className="text-xs text-slate-400 mb-3">
          ❌ Passed: {auction.passers.map(id => state.managers.find(m => m.id === id)?.name).join(', ')}
        </div>
      )}

      {/* Ready to sell banner */}
      {allOthersOut && (
        <div className="bg-emerald-700/30 border-2 border-emerald-500 rounded-lg p-3 mb-4 text-center animate-pulse">
          <div className="text-emerald-300 font-extrabold text-lg">✅ READY TO SELL</div>
          <div className="text-xs text-emerald-200">Everyone else has passed or can't bid.</div>
        </div>
      )}

      {/* Action area */}
      <div className="bg-slate-800/60 rounded-lg p-3">
        <div className="text-sm font-semibold text-white mb-2">
          Your Turn: <span className="text-teal-400">{localManager?.name}</span>
        </div>

        {!isMyTurn && !allOthersOut && (
          <p className="text-amber-400 text-sm">
            Waiting for <span className="font-bold">{state.managers.find(m => m.id === nextId)?.name}</span>…
          </p>
        )}

        {isMyTurn && !canAfford && !isHighBidder && (
          <div className="p-2 mb-3 bg-rose-900/50 border border-rose-500 rounded text-sm text-rose-300 font-semibold">
            ⚠️ Your max bid (${power}) is below the next required bid (${nextRequired}). You must <strong>Pass</strong>.
          </div>
        )}

        {isMyTurn && !allOthersOut && (
          <div className="flex gap-2 items-stretch">
            <input
              type="number"
              min={nextRequired}
              max={power}
              step={1}
              value={bid}
              onChange={e => setBid(Math.max(nextRequired, Math.min(power, Math.floor(Number(e.target.value) || nextRequired))))}
              disabled={!canAfford}
              className="w-24 p-2 text-center font-bold bg-slate-900 border border-slate-700 rounded text-white"
            />
            <button
              onClick={() => onBid(bid)}
              disabled={!canAfford || bid < nextRequired || bid > power}
              className="btn-danger flex-1"
            >🔨 Bid ${bid}{valueAtMyBid && bid > 0 && (
              <span className={`ml-2 pill text-[10px] text-white ${valueAtMyBid.bgClass}`}>{valueAtMyBid.label}</span>
            )}</button>
            <div className="flex flex-col gap-1">
              <button onClick={() => setBid(b => Math.min(power, b + 1))} className="btn-ghost px-2 py-0.5 text-xs">+1</button>
              <button onClick={() => setBid(b => Math.min(power, b + 5))} className="btn-ghost px-2 py-0.5 text-xs">+5</button>
            </div>
            <button onClick={onPass} className="btn-ghost">Pass</button>
          </div>
        )}

        {/* Sell button (commish only) */}
        {isCommissioner && (
          <div className="flex gap-2 mt-3 pt-3 border-t border-slate-700">
            <button onClick={onSell} className="btn-primary flex-1" disabled={!auction.highBidderId}>
              💥 SOLD! to {highBidder?.name ?? '—'} for ${auction.highBid}
            </button>
            <button onClick={onVeto} className="btn-danger">Veto</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div className="card p-2 text-center">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={`text-lg font-extrabold ${accent}`}>{value}</div>
    </div>
  );
}
